import { __awaiter } from "tslib";
import { DEFAULT_LABELS, FILTERS_IDENTATION } from "./constants";
import { FilterTree, getFieldValueFromItem } from "./SmartTableFilter";
import { FilterValidationErrorEnum, ListFilterOperatorEnum, NumberFilterOperatorEnum, StringFilterOperatorEnum, } from "./enums";
import { TedivoForm, createSelectShoelace, } from "@tedivo/tedivo-form";
import { IntegratedDialog } from "@tedivo/tedivo-ui";
import { SmartTableFiltersMgmt } from "./SmartTableFiltersMgmt";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";
import { z } from "zod";
var conditionAddActions;
(function (conditionAddActions) {
    conditionAddActions[conditionAddActions["AND_SIBLING"] = 0] = "AND_SIBLING";
    conditionAddActions[conditionAddActions["OR_SIBLING"] = 1] = "OR_SIBLING";
    conditionAddActions[conditionAddActions["AND_GROUP"] = 2] = "AND_GROUP";
    conditionAddActions[conditionAddActions["OR_GROUP"] = 3] = "OR_GROUP";
})(conditionAddActions || (conditionAddActions = {}));
export class SmartTableFilterDialog {
    constructor({ options, data, onSave, labels, }) {
        this._isOpen = false;
        this._showPrettyHtml = false;
        this._enableStoring = false;
        this._selectFieldValues = {};
        this.listOfSavedFilters = [];
        this.currentSavedFilterId = undefined;
        // HTML components
        this._dialog = undefined;
        this._nodesForStoringFilters = {
            btnSaveFilter: document.createElement("sl-button"),
            dropdownSavedFilters: document.createElement("sl-select"),
            iconDropdownSaveF: document.createElement("sl-icon"),
            iconDropdownCog2: document.createElement("sl-icon"),
            divNodeSaveFilter: document.createElement("div"),
            divHeaderActions: document.createElement("div"),
            filterSettingsBtn: document.createElement("sl-button"),
        };
        this.openDialogForStoreFilterName = (currentFilterId, callback) => {
            var _a, _b;
            const existingFilters = this.listOfSavedFilters;
            const labels = this.labels;
            const hiddenData = {
                savedFilterId: "",
                name: ((_a = existingFilters.find((f) => f.id === currentFilterId)) === null || _a === void 0 ? void 0 : _a.name) || "",
            };
            let initialName = hiddenData.name;
            if (!currentFilterId) {
                const conditionPrettifiedHtmlElement = this._filterTree.toPrettyHtmlElement((field) => { var _a; return ((_a = this._fields.get(field)) === null || _a === void 0 ? void 0 : _a.label) || String(field); }, (value, field) => getFieldValueFromItem(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                { [field]: value }, field, this._fields), {
                    inline: true,
                });
                const innerTexts = getInnerTextsOfNode(conditionPrettifiedHtmlElement);
                initialName = innerTexts
                    .join(" ")
                    .trim()
                    .replace(/^\(/, "")
                    .replace(/\)$/, "")
                    .trim();
            }
            else {
                hiddenData.savedFilterId = currentFilterId;
            }
            const dialog = new IntegratedDialog({
                parentNode: document.body,
                buttonsAttrs: [
                    {
                        label: labels.cancel,
                        type: "button",
                        value: "cancel",
                        variant: "text",
                    },
                    {
                        label: labels.saveFilter,
                        type: "submit",
                        value: "save",
                        variant: "primary",
                        disableOnClick: true,
                    },
                ],
            });
            const form = new TedivoForm({
                formProps: {
                    autoFocusOnFirstInput: true,
                },
                fields: [
                    {
                        name: "savedFilterId",
                        type: "select",
                        label: labels.replaceFilter,
                        options: [
                            {
                                id: "",
                                name: `- ${labels.none} -`,
                            },
                            ...existingFilters,
                        ].map((filter) => ({
                            value: filter.id,
                            label: filter.name,
                        })),
                        hoist: true,
                        initialValue: currentFilterId,
                    },
                    {
                        name: "name",
                        type: "textBox",
                        label: labels.name,
                        helpText: labels.filterNameUnique,
                        initialValue: initialName,
                    },
                ],
                onSubmit: () => undefined,
                hiddenData,
                formValidator: z
                    .object({
                    savedFilterId: z.string(),
                    name: z.string(),
                })
                    .refine((d) => {
                    if (d.savedFilterId)
                        return true;
                    const filterNameExists = existingFilters.find((f) => f.name.toUpperCase() === d.name.toUpperCase());
                    return !filterNameExists || filterNameExists.id === d.savedFilterId;
                }, {
                    path: ["name"],
                    message: labels.filterNameAlreadyExists,
                }),
                submitButton: (_b = dialog.getButton("save")) !== null && _b !== void 0 ? _b : document.createElement("sl-button"),
            });
            dialog.onButtonClicked = (acc) => {
                form.hasSubmittedAlready = true;
                if (acc === "save") {
                    const validationResult = form.execValidation();
                    if (validationResult.success) {
                        const { savedFilterId, name } = form.getValues();
                        callback(name, savedFilterId);
                        dialog.hide();
                        return true;
                    }
                    else {
                        return false;
                    }
                }
                return true;
            };
            dialog.show(labels.storeFilter, form.form);
        };
        this.labels = labels !== null && labels !== void 0 ? labels : DEFAULT_LABELS;
        this._fields = options.fields;
        this._showPrettyHtml = !!options.showPrettyHtml;
        this._enableStoring = !!options.enableStoring;
        this._userOptionsStorage = options.userOptionsStorage;
        this._filterTree = new FilterTree({
            labels: this.labels,
            fieldsByKey: undefined,
        });
        this._data = data;
        this._fieldSelectOptions = Array.from(this._fields.entries())
            .filter(([, field]) => !field.notFilterable)
            .map((entry) => ({
            name: entry[1].label,
            value: entry[0].toString(),
        }));
        this._dialog = new IntegratedDialog({
            parentNode: document.body,
            width: "1000px",
            maxWidth: "90vw",
            bodyOverflow: "auto",
            removeDialogWhenHidden: true,
            buttonsAttrs: [
                {
                    value: "cancel",
                    label: this.labels.cancel,
                    variant: "neutral",
                    type: "button",
                },
                {
                    value: "clear",
                    label: this.labels.clear,
                    variant: "primary",
                    type: "button",
                    icon: "x-lg",
                },
                {
                    value: "apply",
                    label: this.labels.save,
                    variant: "primary",
                    type: "submit",
                    icon: "funnel",
                },
            ],
        });
        this._dialog.onButtonClicked = (v) => {
            if (v === "cancel") {
                return;
            }
            if (v === "clear") {
                this.currentSavedFilterId = undefined;
                this._nodesForStoringFilters.dropdownSavedFilters.value = "";
                this._filterTree.setFilters({
                    id: this._filterTree.root.id,
                    type: "AND",
                    children: [],
                });
                onSave(this._filterTree.getFilters());
                return;
            }
            if (v === "apply") {
                if (this._filterTree.isEmptyFilter()) {
                    onSave({ id: this._filterTree.root.id, type: "AND", children: [] });
                    return;
                }
                const issues = this._filterTree.getValidationResults();
                if (issues.length > 0) {
                    highlightInvalidNodes(issues, {
                        scrollToFirst: true,
                        labels: this.labels,
                    });
                    return false;
                }
                onSave(this._filterTree.getFilters());
                return;
            }
            return false;
        };
        // Adapt the HTML nodes for storing filters
        {
            const divHeaderActions = this._nodesForStoringFilters.divHeaderActions;
            divHeaderActions.className = "custom-header-actions";
            divHeaderActions.slot = "header-actions";
            const iconDropdownSaveF = this._nodesForStoringFilters.iconDropdownSaveF;
            iconDropdownSaveF.name = "box-arrow-in-up";
            iconDropdownSaveF.slot = "prefix";
            const dropdownSavedFilters = this._nodesForStoringFilters.dropdownSavedFilters;
            dropdownSavedFilters.style.width = "300px";
            dropdownSavedFilters.pill = true;
            const iconCog2 = this._nodesForStoringFilters.iconDropdownCog2;
            iconCog2.name = "gear-fill";
            const filterSettingsBtn = this._nodesForStoringFilters.filterSettingsBtn;
            filterSettingsBtn.className = "filter-settings-btn";
            filterSettingsBtn.variant = "primary";
            filterSettingsBtn.outline = true;
            filterSettingsBtn.appendChild(document.createTextNode(" "));
            filterSettingsBtn.appendChild(iconCog2);
        }
        this.attachEvents();
        this.fetchLatestFilters();
    }
    attachEvents() {
        this._nodesForStoringFilters.dropdownSavedFilters.addEventListener("sl-change", () => {
            const selectedValue = this._nodesForStoringFilters.dropdownSavedFilters
                .value;
            const filterData = this.listOfSavedFilters.find((filter) => filter.id === selectedValue);
            if (filterData) {
                this.currentSavedFilterId = selectedValue;
                this._filterTree.setFilters(filterData.filterDefinition);
                this.show(this._filterTree);
            }
        }, false);
        this._nodesForStoringFilters.btnSaveFilter.addEventListener("click", () => {
            const issues = this._filterTree.getValidationResults();
            if (issues.length > 0) {
                highlightInvalidNodes(issues, {
                    scrollToFirst: true,
                    labels: this.labels,
                });
                return;
            }
            this.openDialogForStoreFilterName(this._nodesForStoringFilters.dropdownSavedFilters.value, (name, id) => __awaiter(this, void 0, void 0, function* () {
                if (!this._userOptionsStorage)
                    return;
                const newId = yield this._userOptionsStorage.storeFilterTree(this._filterTree.toJSON(), id, name, this._userOptionsStorage.storageKey);
                this.fetchLatestFilters().then(() => {
                    this._updateStoredFiltersUI(newId);
                });
            }));
        });
        this._nodesForStoringFilters.filterSettingsBtn.addEventListener("click", () => {
            new SmartTableFiltersMgmt(this.labels, document.body, this.listOfSavedFilters, this._fields, this._userOptionsStorage, (newFiltersList) => {
                this.listOfSavedFilters = newFiltersList;
                this._updateStoredFiltersUI(this.currentSavedFilterId);
            });
        });
    }
    get isOpen() {
        return this._isOpen;
    }
    show(currentFilter) {
        var _a;
        if (!this._dialog)
            return;
        this._filterTree.setFilters(currentFilter.getFilters());
        const content = this._buildHtml();
        (_a = this._dialog) === null || _a === void 0 ? void 0 : _a.show(this.labels.title, content);
        this.fetchLatestFilters().then(() => {
            this._updateStoredFiltersUI(this.currentSavedFilterId);
        });
        return this;
    }
    _buildHtml() {
        const filter = this._filterTree;
        const holder = document.createElement("div");
        holder.style.setProperty("--and-label", `"${this.labels.and}"`);
        holder.style.setProperty("--or-label", `"${this.labels.or}"`);
        holder.className = "smart-table-filter-dialog";
        const filterRegion = document.createElement("div");
        filterRegion.className = "filters-region";
        const filterTextRegion = document.createElement("div");
        const form = document.createElement("form");
        form.className = "smart-table-validation-form";
        form.noValidate = true; // Prevent browser default
        form.appendChild(filterRegion);
        if (this._showPrettyHtml) {
            const filterTextTitle = document.createElement("div");
            filterTextTitle.classList.add("filter-text-title");
            filterTextRegion.appendChild(filterTextTitle);
            filterTextTitle.textContent = this.labels.filterPreviewTitle;
            filterTextRegion.className = "filter-text-region";
            const filterText = this._filterTree.toPrettyHtmlElement();
            filterTextRegion.appendChild(filterText);
        }
        if (this._enableStoring) {
            createHtmlNodesForStoringFilters(this._dialog, this._nodesForStoringFilters.btnSaveFilter, this._nodesForStoringFilters.divNodeSaveFilter, this._nodesForStoringFilters.dropdownSavedFilters, this._nodesForStoringFilters.filterSettingsBtn, this.labels);
        }
        holder.appendChild(form);
        holder.appendChild(filterTextRegion);
        if (this._filterTree.root.children.length === 0) {
            this._filterTree.insertEmptyCondition(this._filterTree.root.id);
        }
        this._renderFilterNode(filter.getFilters(), filterRegion);
        return holder;
        function createHtmlNodesForStoringFilters(dialog, btnSaveFilter, divNodeSaveFilter, dropdownSavedFilters, filterSettingsBtn, labels) {
            const div = divNodeSaveFilter;
            div.className = `node-at-start`;
            const tooltip = document.createElement("sl-tooltip");
            tooltip.content = labels.storeFilterHelpText;
            btnSaveFilter.innerHTML = labels.storeFilter;
            btnSaveFilter.variant = "success";
            btnSaveFilter.outline = true;
            const btnSaveIcon = document.createElement("sl-icon");
            btnSaveIcon.name = "box-arrow-in-down";
            btnSaveIcon.slot = "prefix";
            btnSaveFilter.appendChild(btnSaveIcon);
            tooltip.appendChild(btnSaveFilter);
            div.appendChild(filterSettingsBtn);
            div.appendChild(tooltip);
            div.appendChild(dropdownSavedFilters);
            if (dialog)
                dialog.buttonsExtraNode = div;
        }
    }
    _updateHtml() {
        var _a;
        const newContent = this._buildHtml();
        (_a = this._dialog) === null || _a === void 0 ? void 0 : _a.show(this.labels.title, newContent);
    }
    _renderFilterNode(filter, parentElement, depth = 0, parentFilterElement, parentAndContainer, parentOrContainer, parentOrBoxAndContainer, removeOrGroupHeightFromAndGroup, isGroupAndLastChildOfGroup, isOrBlock, addAndText) {
        if (!isGroup(filter)) {
            this._renderConditionRow(filter, parentFilterElement, parentElement, depth, parentAndContainer, parentOrContainer, isOrBlock, addAndText);
            return;
        }
        const group = filter;
        const groupWrapper = document.createElement("div");
        groupWrapper.className = "group-wrapper";
        const groupContainer = document.createElement("div");
        groupContainer.className = `${filter.type.toLowerCase()}-group-container`;
        groupContainer.style.marginLeft = `${depth * FILTERS_IDENTATION}px`;
        groupContainer.setAttribute("id", filter.id);
        parentElement.appendChild(groupWrapper);
        groupWrapper.appendChild(groupContainer);
        const andContainer = isAndGroup(filter) ? groupContainer : undefined;
        let orContainer = isOrGroup(filter) ? groupContainer : undefined;
        if (isOrGroup(filter)) {
            const addButton = this._createConditionAddButton(filter, parentFilterElement, parentElement, true);
            groupWrapper.appendChild(addButton);
        }
        let orBoxAndContainer = parentOrBoxAndContainer;
        let isFirstChildOfAndGroupAnOrGroup = false;
        let shouldDisplayAndLabel = false;
        let childIsGroupAndLastChildOfGroup = isGroupAndLastChildOfGroup;
        group.children.forEach((condition, index) => {
            // Draw the lines between AND and OR filters
            if (isOrGroup(group) && orContainer) {
                ({ orBoxAndContainer, depth, childIsGroupAndLastChildOfGroup } =
                    this._handleOrGroupChild(condition, index, depth, parentElement, parentFilterElement, orContainer, removeOrGroupHeightFromAndGroup, isGroupAndLastChildOfGroup, parentAndContainer, orBoxAndContainer, index === group.children.length - 1));
            }
            else if (isAndGroup(filter) && andContainer) {
                ({
                    firstChildOfAndIsOr: isFirstChildOfAndGroupAnOrGroup,
                    shouldHaveAndText: shouldDisplayAndLabel,
                    orContainer,
                    orBoxAndContainer,
                    childIsGroupAndLastChildOfGroup,
                } = this._handleAndGroupChild(condition, index, andContainer, parentOrContainer, isGroupAndLastChildOfGroup, index === group.children.length - 1));
            }
            this._renderFilterNode(condition, groupContainer, depth, filter, andContainer, orContainer, orBoxAndContainer, isFirstChildOfAndGroupAnOrGroup, childIsGroupAndLastChildOfGroup, isOrGroup(group), shouldDisplayAndLabel);
            if (filter.type === "AND") {
                depth = depth + 1;
            }
        });
    }
    _handleOrGroupChild(condition, index, parentDepth, parentElement, parentFilterElement, orContainer, substractOrGroupHeightFromAndGroupTop, substractGroupHeighFromAndGroupParentBottom, parentAndContainer, parentOrBoxAndContainer, isLastChild) {
        let orBoxAndContainer = parentOrBoxAndContainer;
        let depth = parentDepth;
        if (index === 0) {
            depth = 0;
            if (parentFilterElement && isAndGroup(parentFilterElement)) {
                const isFirstChildInParentAnd = parentFilterElement.children[0].id === orContainer.id;
                if (!isFirstChildInParentAnd) {
                    // Or group parent is an AND group, and it's not first child, a line has to join AND group line and OR group box.
                    const orBoxAndLine = document.createElement("div");
                    orContainer.appendChild(orBoxAndLine);
                    orBoxAndLine.className = "orBox-and-line";
                    requestAnimationFrame(() => drawLineBetweenGroups(orContainer, parentElement, orBoxAndLine, "--orBox-and-line-length", "::before"));
                }
                else {
                    requestAnimationFrame(() => {
                        // If the OR group is first child of AND group, that is inside of an OR group, a line should be placed from the or group, to the grandparent or group line
                        const grandParentOr = this._filterTree.findParentNode(parentElement.id);
                        const grandParentOrElement = document.getElementById((grandParentOr === null || grandParentOr === void 0 ? void 0 : grandParentOr.id) || "");
                        if (grandParentOr &&
                            isOrGroup(grandParentOr) &&
                            grandParentOrElement) {
                            const orBoxOrLine = document.createElement("div");
                            parentElement.appendChild(orBoxOrLine);
                            orBoxOrLine.className = "orBox-or-line";
                            drawLineBetweenGroups(parentElement, grandParentOrElement, orBoxOrLine, "--orBox-or-line-length", "::before");
                        }
                    });
                }
            }
            if (substractOrGroupHeightFromAndGroupTop && parentAndContainer) {
                // If first child of an AND group is an OR group, AND group line have to start where the OR group ends.
                requestAnimationFrame(() => {
                    const rect = orContainer.getBoundingClientRect();
                    const styles = window.getComputedStyle(orContainer);
                    const marginBottom = parseFloat(styles.marginBottom);
                    const totalHeight = rect.height + marginBottom;
                    parentAndContainer.classList.add(`and-group-top-sliced-container`);
                    parentAndContainer.style.setProperty("--and-line-top", `${totalHeight}px`);
                });
            }
            if (substractGroupHeighFromAndGroupParentBottom && parentAndContainer) {
                //If this group is last child of an AND group, we recalculate the and line length
                requestAnimationFrame(() => {
                    const rect = orContainer.getBoundingClientRect();
                    parentAndContainer.classList.add(`and-group-bottom-sliced-container`);
                    const andLineBottom = rect.height - 20;
                    parentAndContainer.style.setProperty("--and-line-bottom", `${andLineBottom}px`);
                });
            }
        }
        else {
            orBoxAndContainer = undefined;
        }
        return {
            orBoxAndContainer,
            depth,
            childIsGroupAndLastChildOfGroup: isLastChild && isGroup(condition),
        };
    }
    _handleAndGroupChild(condition, index, andContainer, parentOrContainer, substractGroupHeighFromOrGroupParentBottom, isLastChild) {
        let firstChildOfAndIsOr = false;
        let shouldHaveAndText = false;
        let orContainer = undefined;
        const orBoxAndContainer = undefined;
        if (index === 0) {
            orContainer = parentOrContainer;
            if (isOrGroup(condition)) {
                firstChildOfAndIsOr = true;
            }
            if (substractGroupHeighFromOrGroupParentBottom && parentOrContainer) {
                //If this group is last child of a OR group, we calculate the or line length and move OR text
                requestAnimationFrame(() => {
                    const rect = andContainer.getBoundingClientRect();
                    const parentRect = parentOrContainer.getBoundingClientRect();
                    parentOrContainer.classList.add(`or-group-bottom-sliced-container`);
                    const orLineBottom = rect.height - 35;
                    parentOrContainer.style.setProperty("--or-line-bottom", `${orLineBottom}px`);
                    parentOrContainer.style.setProperty("--or-line-text-translate-top", `${(parentRect.height - orLineBottom + 35) * 0.5}px`);
                });
            }
        }
        else {
            shouldHaveAndText = true;
        }
        return {
            firstChildOfAndIsOr,
            shouldHaveAndText,
            orContainer,
            orBoxAndContainer,
            childIsGroupAndLastChildOfGroup: isLastChild && isGroup(condition),
        };
    }
    _renderConditionRow(condition, parentFilterElement, parent, depth, andContainer, orContainer, isOrGroup, addAndText) {
        var _a, _b;
        const conditionDiv = document.createElement("div");
        conditionDiv.setAttribute("data-filter-id", condition.id);
        conditionDiv.setAttribute("data-condition-id", condition.id);
        conditionDiv.className = `condition-row ${addAndText && "add-and-text"}`;
        conditionDiv.style.marginLeft = `${depth * FILTERS_IDENTATION}px`;
        const fieldSelect = this._createFieldSelect(condition);
        const operatorSelect = this._createOperatorSelect(condition);
        conditionDiv.appendChild(fieldSelect);
        if (condition.field) {
            conditionDiv.appendChild(operatorSelect);
        }
        if (condition.operator && condition.field) {
            const fieldType = this._resolveFieldType(condition.field);
            if (fieldType !== "list" && fieldType !== "boolean") {
                const valueInput = this._createValueInput(condition, (_b = (_a = condition.value) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : "", (inputValue) => {
                    condition.value = inputValue;
                }, "filter-value");
                conditionDiv.appendChild(valueInput);
            }
            else {
                const valueSelect = this._createListSelect(condition);
                conditionDiv.appendChild(valueSelect);
            }
        }
        if (isOrGroup || orContainer) {
            const orLine = document.createElement("div");
            orLine.className = "or-line";
            conditionDiv.appendChild(orLine);
        }
        const deleteButton = this._createDeleteButton(condition.id);
        const addButton = this._createConditionAddButton(condition, parentFilterElement, andContainer);
        const actionBox = document.createElement("div");
        actionBox.className = "action-box";
        actionBox.appendChild(addButton);
        actionBox.appendChild(deleteButton);
        conditionDiv.appendChild(actionBox);
        parent.appendChild(conditionDiv);
        requestAnimationFrame(() => {
            if (andContainer) {
                const metrics = calculateLineLengthAndPosition(conditionDiv, andContainer, "::before");
                setLineStyle(conditionDiv, "--and-line-length", metrics);
            }
            if (orContainer) {
                const metrics = calculateLineLengthAndPosition(conditionDiv, orContainer, "::before");
                setLineStyle(conditionDiv, "--or-line-length", metrics);
            }
        });
    }
    _createFieldSelect(condition) {
        const errorMessageWrapper = document.createElement("div");
        errorMessageWrapper.className = "smart-error-text-wrapper";
        errorMessageWrapper.appendChild(this._createSelect({
            id: "field-select",
            className: "filter-field",
            value: condition.field,
            placeholder: "Field",
            options: this._fieldSelectOptions,
            onChange: (field, oldField) => {
                const currentField = field && this._fields.get(field);
                const previousField = oldField && this._fields.get(oldField);
                const fieldChanged = !!field && !!oldField;
                const listTypeChanged = (currentField === null || currentField === void 0 ? void 0 : currentField.isList) !== (previousField === null || previousField === void 0 ? void 0 : previousField.isList);
                const fieldTypeChanged = (currentField === null || currentField === void 0 ? void 0 : currentField.type) !== (previousField === null || previousField === void 0 ? void 0 : previousField.type);
                const hasFieldTypeOrListChanged = fieldChanged && (listTypeChanged || fieldTypeChanged);
                let newOperator = condition.operator;
                if (!field || !oldField || hasFieldTypeOrListChanged) {
                    const newFilterOptions = field
                        ? this._getFilterTypeOptions((currentField === null || currentField === void 0 ? void 0 : currentField.type) || "")
                        : [];
                    const isOperatorValid = condition.operator &&
                        newFilterOptions.some((opt) => opt.value === condition.operator) &&
                        !listTypeChanged;
                    if (!field || (hasFieldTypeOrListChanged && !isOperatorValid)) {
                        newOperator = undefined;
                    }
                }
                if (field)
                    this._filterTree.updateNode(condition.id, {
                        field: field,
                        operator: newOperator,
                        value: undefined,
                    });
                this._updateHtml();
            },
        }));
        return errorMessageWrapper;
    }
    _createOperatorSelect(condition) {
        const errorMessageWrapper = document.createElement("div");
        errorMessageWrapper.className = "smart-error-text-wrapper";
        const fieldType = condition.field
            ? this._resolveFieldType(condition.field)
            : undefined;
        errorMessageWrapper.appendChild(this._createSelect({
            id: "operator-select",
            placeholder: "Operator",
            className: "filter-operator",
            value: condition.operator,
            options: fieldType ? this._getFilterTypeOptions(fieldType) : [],
            onChange: (operator) => {
                this._filterTree.updateNode(condition.id, {
                    operator,
                });
                this._updateHtml();
            },
        }));
        return errorMessageWrapper;
    }
    _createListSelect(condition) {
        const errorMessageWrapper = document.createElement("div");
        errorMessageWrapper.className = "smart-error-text-wrapper";
        let value = condition.value;
        if (!Array.isArray(value)) {
            value = value ? [value.toString()] : undefined;
        }
        let options = [];
        if (condition.field &&
            this._resolveFieldType(condition.field) === "boolean") {
            options = [
                { name: this.labels.booleanTrueValue, value: true },
                { name: this.labels.booleanFalseValue, value: false },
            ];
        }
        else {
            options = condition.field
                ? this._getUniqueFieldValues(condition.field).map(({ label, value }) => ({
                    name: label,
                    value,
                }))
                : [];
        }
        const valueSelect = this._createSelect({
            id: "field-list-value-select",
            className: "filter-list-value",
            value: value,
            placeholder: this.labels.selectOperator,
            multi: true,
            options,
            onChange: (value) => {
                this._filterTree.updateNode(condition.id, {
                    value,
                });
                this._updateHtml();
            },
        });
        errorMessageWrapper.appendChild(valueSelect);
        return errorMessageWrapper;
    }
    _getUniqueFieldValues(field) {
        // Cache the values for the field
        if (this._selectFieldValues[field]) {
            return this._selectFieldValues[field];
        }
        const uniqueValues = new Map();
        this._data.forEach((item) => {
            const value = item[field];
            const label = getFieldValueFromItem(item, field, this._fields);
            if (value !== undefined &&
                value !== null &&
                !uniqueValues.has(String(value))) {
                uniqueValues.set(String(value), {
                    value: String(value),
                    label: String(label),
                });
            }
        });
        const result = Array.from(uniqueValues)
            .sort((a, b) => a[1].label.localeCompare(b[1].label))
            .map(([, v]) => v);
        this._selectFieldValues[field] = result;
        return result;
    }
    _createValueInput(condition, value, onChange, className) {
        const errorMessageWrapper = document.createElement("div");
        errorMessageWrapper.className = "smart-error-text-wrapper";
        const field = condition.field && this._fields.get(condition.field);
        const valueInput = document.createElement("sl-input");
        valueInput.className = "filter-value";
        valueInput.type = field && field.type === "number" ? field === null || field === void 0 ? void 0 : field.type : "text";
        valueInput.value = value;
        if (className)
            valueInput.classList.add(className);
        valueInput.addEventListener("sl-change", (ev) => {
            const target = ev.target;
            this._filterTree.updateNode(condition.id, {
                value: target.value,
            });
            this._updateHtml();
            onChange(target.value);
        });
        errorMessageWrapper.appendChild(valueInput);
        return errorMessageWrapper;
    }
    _createDeleteButton(conditionId) {
        const deleteButton = document.createElement("sl-icon-button");
        deleteButton.className = "delete-button";
        deleteButton.name = "trash";
        deleteButton.label = this.labels.deleteCondition;
        deleteButton.addEventListener("click", () => {
            this._filterTree.removeNode(conditionId);
            this._updateHtml();
        });
        return deleteButton;
    }
    _createConditionAddButton(condition, parentFilterElement, andContainer, isOrGroup) {
        const options = andContainer
            ? [
                andContainer && parentFilterElement
                    ? {
                        name: this.labels.addAndSibling,
                        value: conditionAddActions.AND_SIBLING,
                    }
                    : undefined,
                !isOrGroup && andContainer
                    ? {
                        name: this.labels.createOrGroup,
                        value: conditionAddActions.OR_GROUP,
                    }
                    : undefined,
            ]
            : [
                !andContainer && parentFilterElement
                    ? {
                        name: this.labels.addOrSibling,
                        value: conditionAddActions.OR_SIBLING,
                    }
                    : undefined,
                !andContainer
                    ? {
                        name: this.labels.createAndGroup,
                        value: conditionAddActions.AND_GROUP,
                    }
                    : undefined,
            ];
        return createSelectShoelace({
            className: "add-button",
            buttonNodeType: "sl-icon-button",
            buttonText: "",
            variant: "text",
            hoist: true,
            id: `add-btn-id-${condition.id}`,
            iconPrefix: "plus-lg",
            options: options.filter((x) => x !== undefined),
            onChange: (value) => {
                switch (value) {
                    case conditionAddActions.AND_SIBLING.toString():
                    case conditionAddActions.OR_SIBLING.toString():
                        if (parentFilterElement && isGroup(parentFilterElement)) {
                            const currentConditionIndex = parentFilterElement.children.findIndex((p) => p.id === condition.id);
                            this._filterTree.insertEmptyCondition(parentFilterElement.id, currentConditionIndex >= 0
                                ? currentConditionIndex + 1
                                : undefined);
                        }
                        break;
                    case conditionAddActions.AND_GROUP.toString():
                        this._filterTree.wrapWithGroupAndAddEmptyCondition(condition.id, "AND");
                        break;
                    case conditionAddActions.OR_GROUP.toString():
                        this._filterTree.wrapWithGroupAndAddEmptyCondition(condition.id, "OR");
                        break;
                    default:
                        break;
                }
                this._updateHtml();
            },
        });
    }
    _resolveFieldType(field) {
        const fieldMeta = this._fields.get(field);
        if (fieldMeta === null || fieldMeta === void 0 ? void 0 : fieldMeta.isList)
            return "list";
        if ((fieldMeta === null || fieldMeta === void 0 ? void 0 : fieldMeta.type) === "boolean")
            return "boolean";
        if ((fieldMeta === null || fieldMeta === void 0 ? void 0 : fieldMeta.type) === "number")
            return "number";
        return "string";
    }
    _createSelect({ value, options, onChange, id, className, multi, placeholder, }) {
        let processedValue = String(value);
        if (multi && Array.isArray(value)) {
            processedValue = value.join(" ");
        }
        const select = document.createElement("sl-select");
        select.id = id;
        select.required = true;
        select.placeholder = placeholder !== null && placeholder !== void 0 ? placeholder : "";
        select.classList.add(className);
        select.multiple = multi !== null && multi !== void 0 ? multi : false;
        select.setAttribute("help-text", ""); // Enables help/error message area
        options.forEach((o) => {
            if (o.value === undefined)
                return;
            const option = document.createElement("sl-option");
            option.value = String(o.value);
            option.innerHTML = o.name;
            select.appendChild(option);
        });
        select.addEventListener("sl-change", (ev) => {
            onChange(ev.target.value, value);
        });
        select.value = processedValue;
        select.hoist = true;
        return select;
    }
    _getFilterTypeOptions(filterType) {
        let enumObject;
        let translations;
        switch (filterType) {
            case "number":
                enumObject = NumberFilterOperatorEnum;
                translations = this.labels.operators.NumberFilterOperatorEnum;
                break;
            case "list":
            case "boolean":
                enumObject = ListFilterOperatorEnum;
                translations = this.labels.operators.ListFilterOperatorEnum;
                break;
            default:
                enumObject = StringFilterOperatorEnum;
                translations = this.labels.operators.StringFilterOperatorEnum;
        }
        return Object.values(enumObject).map((op) => {
            var _a;
            return ({
                name: (_a = translations[op]) !== null && _a !== void 0 ? _a : op,
                value: op,
            });
        });
    }
    fetchLatestFilters() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._userOptionsStorage)
                return;
            try {
                const savedFilters = yield this._userOptionsStorage.fetchAllFilterTrees();
                this.listOfSavedFilters = savedFilters.sort((a, b) => a.name.localeCompare(b.name));
            }
            catch (error) {
                console.warn("Error fetching saved filters:", error);
            }
        });
    }
    _updateStoredFiltersUI(currId) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const savedFilters = this.listOfSavedFilters;
            const dropdownSavedFilters = this._nodesForStoringFilters.dropdownSavedFilters;
            removeChildren(dropdownSavedFilters);
            // Base option
            const option = document.createElement("sl-option");
            option.value = "";
            option.innerHTML = `- ${this.labels.selectStoredFilter} -`;
            dropdownSavedFilters.appendChild(option);
            // Add saved filters
            savedFilters.forEach((filter) => {
                const option = document.createElement("sl-option");
                option.value = filter.id;
                option.innerHTML = filter.name;
                dropdownSavedFilters.appendChild(option);
            });
            // Set the current selected value
            if (currId && currId === ((_a = savedFilters.find((f) => f.id === currId)) === null || _a === void 0 ? void 0 : _a.id)) {
                dropdownSavedFilters.value = currId;
            }
            else {
                dropdownSavedFilters.value = "";
            }
            dropdownSavedFilters.appendChild(this._nodesForStoringFilters.iconDropdownSaveF);
        });
    }
}
export function highlightInvalidNodes(issues, { scrollToFirst = true, labels }) {
    const issueMap = new Map(issues.map((i) => [i.id, i.errors]));
    const allConditionElements = document.querySelectorAll("[data-filter-id]");
    let firstErrorEl;
    allConditionElements.forEach((el) => {
        var _a, _b;
        const nodeId = el.getAttribute("data-filter-id");
        if (!nodeId)
            return;
        const errors = issueMap.get(nodeId);
        // Clear all previous state
        el.classList.remove("has-condition-error");
        const allInputs = Array.from(el.querySelectorAll("sl-input, sl-select"));
        for (const input of allInputs) {
            input.removeAttribute("data-user-invalid");
            input.removeAttribute("data-user-valid");
            input.removeAttribute("help-text");
            (_a = input.setCustomValidity) === null || _a === void 0 ? void 0 : _a.call(input, "");
        }
        if (errors === null || errors === void 0 ? void 0 : errors.length) {
            el.classList.add("has-condition-error");
            for (const error of errors) {
                const message = getValidationMessage(error, labels);
                let target = null;
                if (error === FilterValidationErrorEnum.MISSING_FIELD) {
                    target = el.querySelector("sl-select.filter-field");
                }
                else if (error === FilterValidationErrorEnum.MISSING_OPERATOR) {
                    target = el.querySelector("sl-select.filter-operator");
                }
                else if (error === FilterValidationErrorEnum.MISSING_VALUE) {
                    const input = el.querySelector("sl-input.filter-value");
                    const list = el.querySelector("sl-select.filter-list-value");
                    target = input && input.offsetParent !== null ? input : list;
                    // THIS IS PREVENTING THE INPUT FROM SHOWING THE ERROR, WHY?
                    // if (
                    //   input &&
                    //   input === target &&
                    //   typeof input.value === "string" &&
                    //   input.value.trim() === ""
                    // ) {
                    //   continue;
                    // }
                }
                if (target) {
                    target.setAttribute("data-user-invalid", "");
                    target.removeAttribute("data-user-valid");
                    target.setAttribute("help-text", message);
                    (_b = target.setCustomValidity) === null || _b === void 0 ? void 0 : _b.call(target, " ");
                }
            }
            if (!firstErrorEl)
                firstErrorEl = el;
        }
        else {
            for (const input of allInputs) {
                input.setAttribute("data-user-valid", "");
                input.removeAttribute("data-user-invalid");
            }
        }
    });
    if (scrollToFirst && firstErrorEl) {
        setTimeout(() => {
            var _a;
            firstErrorEl === null || firstErrorEl === void 0 ? void 0 : firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" });
            (_a = firstErrorEl === null || firstErrorEl === void 0 ? void 0 : firstErrorEl.focus) === null || _a === void 0 ? void 0 : _a.call(firstErrorEl);
        }, 100);
    }
}
function isGroup(node) {
    return "children" in node && !("field" in node);
}
function isAndGroup(node) {
    return isGroup(node) && node.type === "AND";
}
function isOrGroup(node) {
    return isGroup(node) && node.type === "OR";
}
function calculateLineLengthAndPosition(element, container, pseudo = "", considerElementPadding = true) {
    const containerRect = container.getBoundingClientRect();
    const containerStyle = window.getComputedStyle(container);
    const containerLineStyle = window.getComputedStyle(container, pseudo);
    const containerLeftOffset = parseFloat(containerLineStyle.left || "0") || 0;
    const containerBordserOffset = parseFloat(containerStyle.borderWidth || "0") || 0;
    const elementRect = element.getBoundingClientRect();
    const elementStyle = window.getComputedStyle(element);
    const elementLeftPaddingOffset = parseFloat(elementStyle.paddingLeft || "0") || 0;
    const elementBorderOffset = parseFloat(elementStyle.borderWidth || "0") || 0;
    const containerX = containerRect.left + containerLeftOffset + containerBordserOffset;
    const elementX = elementRect.left +
        elementBorderOffset +
        (considerElementPadding ? elementLeftPaddingOffset : 0);
    const lineLength = elementX - containerX;
    const lineMoveLeft = lineLength;
    return { lineLength, lineMoveLeft };
}
function setLineStyle(el, lengthVar, { lineLength }) {
    el.style.setProperty(lengthVar, `${lineLength}px`);
}
function drawLineBetweenGroups(fromElement, toElement, target, lengthVar, pseudoElement = "") {
    requestAnimationFrame(() => {
        const { lineLength } = calculateLineLengthAndPosition(fromElement, toElement, pseudoElement, false);
        target.style.setProperty(lengthVar, `${lineLength}px`);
    });
}
const getValidationMessage = (error, labels) => {
    switch (error) {
        case FilterValidationErrorEnum.MISSING_FIELD:
            return labels.selectField;
        case FilterValidationErrorEnum.MISSING_OPERATOR:
            return labels.selectOperator;
        case FilterValidationErrorEnum.MISSING_VALUE:
            return labels.selectValue;
    }
};
function getInnerTextsOfNode(node) {
    const innerTexts = [];
    node.childNodes.forEach((childNode) => {
        if (childNode.nodeType === Node.TEXT_NODE) {
            innerTexts.push(childNode.nodeValue || "");
        }
        else if (childNode.nodeType === Node.ELEMENT_NODE) {
            // Recursively get inner texts of child elements
            const childElement = childNode;
            const childInnerTexts = getInnerTextsOfNode(childElement);
            innerTexts.push(...childInnerTexts);
        }
    });
    return innerTexts;
}
